// EditorFooter.tsx
import React from "react"
import { Tooltip } from "antd"
import { Loader } from "shared/ui/Loader/Loader.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCheck,
  faCircleExclamation,
  faFloppyDisk,
} from "@fortawesome/free-solid-svg-icons"
import OnlinePresence from "./OnlinePresence.component"
import { WebsocketProvider } from "y-websocket"

/**
 * Props interface for the EditorFooter component
 */
interface EditorFooterProps {
  /** Indicates if the questionnaire is currently being saved */
  isSaving: boolean
  /** Indicates if there are unsaved changes in the editor */
  contentChanged: boolean
  /** Indicates if collaboration is connected */
  isCollaborationConnected?: boolean
  /** Indicates if collaboration is still connecting */
  isConnecting?: boolean
  /** Callback to manually save the questionnaire */
  onSave?: () => void
  /** WebSocket provider for online presence */
  provider?: WebsocketProvider | null
  /** Current user ID to exclude from presence list */
  currentUserId?: string
}

/**
 * EditorFooter component displays the status of the editor and provides a save button.
 *
 * @component
 * @param props - The component props
 * @returns The rendered EditorFooter component
 */
const EditorFooter: React.FC<EditorFooterProps> = ({
  isSaving,
  contentChanged,
  isCollaborationConnected = false,
  isConnecting = false,
  onSave,
  provider = null,
  currentUserId,
}) => {
  const saveDisabled = isSaving || !isCollaborationConnected

  const handleSaveClick = () => {
    if (saveDisabled) return
    onSave?.()
  }

  return (
    <div className="flex items-center justify-between bg-gray-50/80 backdrop-blur-sm px-4 py-2.5 h-11 rounded-b-xl border-t border-gray-100">
      {/* Left Side - Connection Status */}
      <div className="flex items-center gap-3">
        {/* Connection Status Indicator */}
        <Tooltip title={
          isConnecting
            ? "Connecting to collaboration server..."
            : isCollaborationConnected
              ? "Your changes sync with other users in real-time"
              : "Changes won't sync with others until reconnected"
        }>
          <div className="flex items-center gap-2 px-3 py-1 bg-white/80 rounded-full border border-gray-200/50 shadow-sm">
            <div className="relative">
              <div
                className={`w-2 h-2 rounded-full ${
                  isConnecting ? "bg-gray-400" : isCollaborationConnected ? "bg-emerald-500" : "bg-amber-400"
                }`}
              />
              {isConnecting && (
                <div className="absolute inset-0 w-2 h-2 rounded-full bg-gray-400 animate-pulse opacity-60" />
              )}
              {isCollaborationConnected && !isConnecting && (
                <div className="absolute inset-0 w-2 h-2 rounded-full bg-emerald-500 animate-ping opacity-50" />
              )}
            </div>
            <span className="text-xs font-medium text-gray-600">
              {isConnecting ? "Connecting..." : isCollaborationConnected ? "Connected" : "Collaboration offline"}
            </span>
          </div>
        </Tooltip>

        {/* Online Presence */}
        {isCollaborationConnected && (
          <OnlinePresence provider={provider} currentUserId={currentUserId} />
        )}

        {/* Collaboration Offline Warning */}
        {!isCollaborationConnected && !isConnecting && (
          <span className="text-xs text-gray-500">
            Edits are saved locally and will sync when reconnected
          </span>
        )}
      </div>

      {/* Right Side - Save Status and Button */}
      <div className="flex items-center gap-3">
        {/* Save Status - don't show Unsaved while connecting */}
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          {isSaving ? (
            <>
              <Loader fontSize={12} color="#9ca3af" />
              <span>Saving</span>
            </>
          ) : contentChanged && !isConnecting ? (
            <>
              <FontAwesomeIcon
                icon={faCircleExclamation}
                className="text-amber-500"
              />
              <span>Unsaved</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon
                icon={faCheck}
                className="text-emerald-500"
              />
              <span>Saved</span>
            </>
          )}
        </div>

        {/* Manual Save Button */}
        {onSave && (
          <Tooltip
            title={
              !isCollaborationConnected
                ? "Can't save while disconnected. Your edits are safe locally and will sync when reconnected"
                : "Save (Ctrl+S)"
            }
          >
            <button
              onClick={handleSaveClick}
              disabled={saveDisabled}
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 bg-white hover:bg-gray-50 rounded-full border border-gray-200 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faFloppyDisk} className="text-gray-500" />
              Save
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

export default EditorFooter
